import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, PasswordInput } from '@mlm/ui';
import {
  checkUsernameAvailable,
  getTerms,
  login,
  previewSignupSponsor,
  signup,
  uploadRegistrationFile,
  type RegistrationFileValue,
  type SponsorPreview,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { prettyError } from '../lib/signup-errors';
import { resolvePostSignupDestination } from '../lib/post-auth';
import { useTenantConfig } from '../storefront/lib/hooks';

/**
 * Member self-signup. Reads `?ref=CODE` from the replicated link, pre-fills
 * the sponsor field. If `ref` is empty, signup falls back to super_admin on
 * the server side (the field can be left blank).
 *
 * On success, auto-logs the user in and navigates to the dashboard.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get('ref') ?? '';
  // Per-package replicated link: the kit product ("package") the prospect
  // arrived through. Carried straight to the server so their dashboard offers
  // only this package after signup. Ignored server-side if not a valid kit.
  const pkgFromUrl = params.get('pkg') ?? '';
  // Replicated CAMPAIGN slug the lead arrived through (`/r/<ref>/<slug>`
  // forwards it as `?c=<slug>`). Carried to the server so a tagged campaign can
  // narrow which activation kits the lead may buy. Unknown slugs are ignored.
  const campaignFromUrl = params.get('c') ?? '';
  // `?buy=<productId>` / `?next=<path>` — where to go once registered. Signup
  // used to hardcode `/dashboard`, which strands someone who came to buy a
  // specific thing (see `resolvePostSignupDestination`).
  const buyFromUrl = params.get('buy');
  const nextFromUrl = params.get('next');
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [username, setUsername] = useState('');
  const [sponsorCode, setSponsorCode] = useState(refFromUrl);
  // "Do you have a sponsor?" — only meaningful when the URL didn't pre-fill one.
  // Default "no" so the server-side default (admin-configured) fills in cleanly.
  const [hasSponsor, setHasSponsor] = useState<boolean>(!!refFromUrl);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Two-step sponsor confirmation: once the form validates and a sponsor code
  // applies, we resolve + show the sponsor and require an explicit Confirm
  // before the account is created.
  const [sponsorPreview, setSponsorPreview] = useState<SponsorPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inlined under the email field when the server returns 409 email_taken,
  // instead of a top-of-form error string. Cleared on any email edit.
  const [emailError, setEmailError] = useState<string | null>(null);

  const passwordsMismatch =
    password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword;

  const termsQ = useQuery({ queryKey: ['terms'], queryFn: getTerms });
  const terms = termsQ.data;
  const requireTerms = !!terms?.requireOnSignup;
  const requireUsername = !!terms?.requireUsernameOnSignup;

  // Registration gating (signup_mode). `links_only` disables the standalone
  // signup page entirely unless the user arrived via a `?ref=` link;
  // `by_sponsor_only` makes the sponsor code mandatory on the standalone page.
  //
  // A campaign page can also count as the invitation (`?c=<slug>`, the ref-less
  // funnel a virtual product opens) when the tenant allows it — those visitors
  // carry no code and are placed under the walk-in default sponsor. Mirrors the
  // server gate in /auth/signup; the server still verifies the slug resolves to
  // an enabled page, so this only decides whether to render the form.
  const tenantConfig = useTenantConfig().data;
  const signupMode = tenantConfig?.signupMode ?? 'on';
  const viaCampaign = !!campaignFromUrl && (tenantConfig?.campaignPageSignupAllowed ?? false);
  const linksOnlyBlocked = signupMode === 'links_only' && !refFromUrl && !viaCampaign;
  const sponsorRequired = signupMode === 'by_sponsor_only' && !refFromUrl && !viaCampaign;

  // Admin-defined extra fields (already sorted by `order` server-side). Text
  // values live as strings; file values as the uploaded object reference.
  const registrationFields = tenantConfig?.registrationFields ?? [];
  const birthDateField = tenantConfig?.birthDateField ?? 'hidden';
  const [regValues, setRegValues] = useState<Record<string, string | RegistrationFileValue>>({});
  // Per-field upload status for file inputs.
  const [regUploading, setRegUploading] = useState<Record<string, boolean>>({});
  const anyRegUploading = Object.values(regUploading).some(Boolean);

  async function onRegFile(fieldId: string, file: File | null) {
    if (!file) {
      setRegValues((v) => {
        const next = { ...v };
        delete next[fieldId];
        return next;
      });
      return;
    }
    setRegUploading((u) => ({ ...u, [fieldId]: true }));
    try {
      const uploaded = await uploadRegistrationFile(file);
      setRegValues((v) => ({ ...v, [fieldId]: uploaded }));
    } catch {
      setError('A file upload failed. Please try a smaller file or a different one.');
    } finally {
      setRegUploading((u) => ({ ...u, [fieldId]: false }));
    }
  }

  // Debounced live username availability check (only when the field is shown).
  const [usernameStatus, setUsernameStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  >('idle');
  const USERNAME_ERROR = 'Please choose an available username to continue.';
  /**
   * The submit-time answer. A fast typist reaches "Create account" while the
   * debounced check is still in flight (or before it fired): asking the server
   * right then is the honest answer, refusing with "choose an available
   * username" while the field says "Username is available." is not.
   */
  async function ensureUsernameAvailable(): Promise<boolean> {
    if (usernameStatus === 'available') return true;
    if (usernameStatus === 'taken' || usernameStatus === 'invalid') return false;
    if (username.length < 3) return false;
    try {
      const r = await checkUsernameAvailable(username);
      const next = !r.valid ? 'invalid' : r.available ? 'available' : 'taken';
      setUsernameStatus(next);
      return next === 'available';
    } catch {
      return false;
    }
  }
  useEffect(() => {
    if (!requireUsername || username.length < 3) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await checkUsernameAvailable(username);
        const next = !r.valid ? 'invalid' : r.available ? 'available' : 'taken';
        setUsernameStatus(next);
        // The check caught up with an earlier refusal: the message is stale.
        if (next === 'available') setError((e) => (e === USERNAME_ERROR ? null : e));
      } catch {
        setUsernameStatus('idle');
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username, requireUsername]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    if (passwordsMismatch) {
      // Belt-and-suspenders — the submit button is also disabled in this state.
      return;
    }
    if (requireTerms && !agreed) {
      setError('Please accept the Terms and Privacy Policy to continue.');
      return;
    }
    if (requireUsername && !(await ensureUsernameAvailable())) {
      setError(USERNAME_ERROR);
      return;
    }
    if (sponsorRequired && !sponsorCode.trim()) {
      setError('An affiliate code is required to sign up.');
      return;
    }
    // Required custom fields must be filled (file fields must finish uploading).
    for (const f of registrationFields) {
      if (!f.required) continue;
      const v = regValues[f.id];
      const empty = v == null || (typeof v === 'string' && v.trim() === '');
      if (empty) {
        setError(`Please complete the required field: ${f.label}.`);
        return;
      }
    }
    if (anyRegUploading) {
      setError('Please wait for file uploads to finish.');
      return;
    }
    // When a sponsor applies, resolve + confirm the sponsor before creating the
    // account. No sponsor (and not required) → create directly as before.
    const useSponsor = (hasSponsor || sponsorRequired) && !!sponsorCode.trim();
    if (useSponsor) {
      setPreviewing(true);
      try {
        const sp = await previewSignupSponsor(sponsorCode.trim());
        setSponsorPreview(sp);
      } catch (err: unknown) {
        const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
        setError(
          code === 'sponsor_not_found'
            ? "We couldn't find that affiliate code. Please check it and try again."
            : prettyError(code),
        );
      } finally {
        setPreviewing(false);
      }
      return;
    }
    await doSignup();
  }

  async function doSignup() {
    setError(null);
    setEmailError(null);
    setSubmitting(true);
    try {
      await signup({
        email,
        password,
        confirmPassword,
        firstName,
        middleName: middleName || undefined,
        lastName,
        phone: phone.trim(),
        username: requireUsername ? username : undefined,
        // Send a sponsor code when the member supplied one (or it's required by
        // signup_mode); otherwise the server fills in the tenant's default.
        sponsorCode: (hasSponsor || sponsorRequired) && sponsorCode ? sponsorCode : undefined,
        pkg: pkgFromUrl || undefined,
        campaignSlug: campaignFromUrl || undefined,
        termsAccepted: agreed,
        termsVersion: terms?.termsVersion,
        registrationData: registrationFields.length > 0 ? regValues : undefined,
        birthDate: birthDateField !== 'hidden' && birthDate ? birthDate : undefined,
      });
      // Auto-login so the user lands with a token — on the dashboard normally,
      // or on the funnel they came through when signup was a step toward buying.
      const loginRes = await login({ identifier: email, password });
      setSession(loginRes.accessToken, loginRes.user);
      navigate(resolvePostSignupDestination({ buy: buyFromUrl, next: nextFromUrl }));
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      const code = data?.error;
      if (code === 'email_taken') {
        setEmailError('That email is already registered. Try signing in.');
      } else if (code === 'username_taken') {
        setUsernameStatus('taken');
        setError('That username is taken. Please choose another.');
      } else if (code === 'username_required') {
        setError('Please choose a username to continue.');
      } else {
        setError(prettyError(code, { viaCampaign: !!campaignFromUrl }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (linksOnlyBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Sign up by invitation only</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Registration is currently available through referral links only. Ask your sponsor for
              their invite or product link to create your account.
            </p>
            <p>
              Already have an account?{' '}
              <a className="underline" href="/login">
                Sign in
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2 — confirm the resolved sponsor before the account is created.
  if (sponsorPreview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Confirm your affiliate code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please confirm this is the correct affiliate code before we create your account.
            </p>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="text-xs text-muted-foreground">You'll be placed under</div>
              <div className="font-medium">
                {sponsorPreview.ownerFullName ?? sponsorPreview.ownerEmail}
              </div>
              <div className="text-sm text-muted-foreground">
                Affiliate Code : {sponsorPreview.slotCode || '—'}
                {sponsorPreview.membershipCode ? ` · ${sponsorPreview.membershipCode}` : ''}
              </div>
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={doSignup} disabled={submitting} className="flex-1">
                {submitting ? 'Creating…' : 'Confirm & create account'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSponsorPreview(null);
                  setError(null);
                }}
                disabled={submitting}
              >
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Create your account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            {/* Subgrid keeps labels and inputs on the same two tracks, so a
                two-line "(optional)" label doesn't push the middle input out
                of alignment with First/Last. Mobile falls back to a single
                column where each FormField stacks naturally. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:grid-rows-[auto_auto] sm:gap-x-3 sm:gap-y-3">
              <FormField
                label="First name"
                htmlFor="firstName"
                className="sm:row-span-2 sm:grid sm:grid-rows-subgrid sm:gap-y-1 sm:space-y-0"
              >
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
              </FormField>
              <FormField
                label={
                  <>
                    Middle name{' '}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </>
                }
                htmlFor="middleName"
                className="sm:row-span-2 sm:grid sm:grid-rows-subgrid sm:gap-y-1 sm:space-y-0"
              >
                <Input
                  id="middleName"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  autoComplete="additional-name"
                />
              </FormField>
              <FormField
                label="Last name"
                htmlFor="lastName"
                className="sm:row-span-2 sm:grid sm:grid-rows-subgrid sm:gap-y-1 sm:space-y-0"
              >
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </FormField>
            </div>
            <FormField label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                autoComplete="email"
                aria-invalid={emailError ? true : undefined}
                required
              />
              {emailError && (
                <p className="text-xs text-destructive mt-1" role="alert">
                  {emailError}
                </p>
              )}
            </FormField>
            {requireUsername && (
              <FormField label="Username" htmlFor="username">
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  minLength={3}
                  maxLength={32}
                  required
                />
                {usernameStatus === 'checking' && (
                  <p className="text-xs text-muted-foreground mt-1">Checking availability…</p>
                )}
                {usernameStatus === 'available' && (
                  <p className="text-xs text-success mt-1">Username is available.</p>
                )}
                {usernameStatus === 'taken' && (
                  <p className="text-xs text-destructive mt-1">That username is taken.</p>
                )}
                {usernameStatus === 'invalid' && (
                  <p className="text-xs text-destructive mt-1">
                    3–32 characters, letters, numbers, and underscores only.
                  </p>
                )}
              </FormField>
            )}
            <FormField label="Password" htmlFor="password">
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </FormField>
            <FormField label="Confirm password" htmlFor="confirmPassword">
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                aria-invalid={passwordsMismatch ? true : undefined}
                required
              />
              {passwordsMismatch && (
                <p className="text-xs text-destructive mt-1" role="alert">
                  Passwords don't match.
                </p>
              )}
            </FormField>
            <FormField label="Contact Number" htmlFor="phone">
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 0917 555 0148"
                required
              />
            </FormField>
            {birthDateField !== 'hidden' && (
              <FormField
                label={
                  birthDateField === 'required' ? (
                    'Birth date'
                  ) : (
                    <>
                      Birth date{' '}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </>
                  )
                }
                htmlFor="birth-date"
              >
                <Input
                  id="birth-date"
                  type="date"
                  value={birthDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setBirthDate(e.target.value)}
                  required={birthDateField === 'required'}
                />
              </FormField>
            )}
            {registrationFields.map((f) => (
              <FormField
                key={f.id}
                label={
                  f.required ? (
                    f.label
                  ) : (
                    <>
                      {f.label}{' '}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </>
                  )
                }
                htmlFor={`reg-${f.id}`}
              >
                {f.type === 'file' ? (
                  <>
                    <Input
                      id={`reg-${f.id}`}
                      type="file"
                      onChange={(e) => onRegFile(f.id, e.target.files?.[0] ?? null)}
                    />
                    {regUploading[f.id] && (
                      <p className="text-xs text-muted-foreground mt-1">Uploading…</p>
                    )}
                    {!regUploading[f.id] &&
                      regValues[f.id] &&
                      typeof regValues[f.id] !== 'string' && (
                        <p className="text-xs text-success mt-1">
                          {(regValues[f.id] as RegistrationFileValue).filename} uploaded.
                        </p>
                      )}
                  </>
                ) : (
                  <Input
                    id={`reg-${f.id}`}
                    value={typeof regValues[f.id] === 'string' ? (regValues[f.id] as string) : ''}
                    onChange={(e) => setRegValues((v) => ({ ...v, [f.id]: e.target.value }))}
                    required={f.required}
                  />
                )}
              </FormField>
            ))}
            {refFromUrl ? (
              <FormField label="Affiliate Code (from invite link)" htmlFor="sponsor">
                <Input
                  id="sponsor"
                  value={sponsorCode}
                  onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
                  readOnly
                  className="bg-muted"
                />
              </FormField>
            ) : sponsorRequired ? (
              <FormField label="Affiliate Code" htmlFor="sponsor">
                <Input
                  id="sponsor"
                  value={sponsorCode}
                  onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
                  placeholder="Affiliate code"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  An affiliate code is required to register.
                </p>
              </FormField>
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">Do you have an affiliate code?</p>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="hasSponsor"
                      checked={!hasSponsor}
                      onChange={() => {
                        setHasSponsor(false);
                        setSponsorCode('');
                      }}
                    />
                    <span>No</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="hasSponsor"
                      checked={hasSponsor}
                      onChange={() => setHasSponsor(true)}
                    />
                    <span>Yes</span>
                  </label>
                </div>
                {hasSponsor && (
                  <Input
                    id="sponsor"
                    value={sponsorCode}
                    onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
                    placeholder="Affiliate code"
                    autoFocus
                  />
                )}
              </div>
            )}
            {requireTerms && (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                />
                <span className="text-muted-foreground">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    Terms and Privacy Policy
                  </button>
                  .
                </span>
              </label>
            )}
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting || previewing || passwordsMismatch || anyRegUploading}
              className="w-full"
            >
              {previewing ? 'Checking sponsor…' : submitting ? 'Creating…' : 'Create account'}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Already have an account?{' '}
              <a className="underline" href="/login">
                Sign in
              </a>
            </p>
          </form>
        </CardContent>
      </Card>

      {showTerms && terms && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
          onClick={() => setShowTerms(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Terms and Privacy Policy"
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-elevated"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-h2">Terms of Service</h2>
            <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
              {terms.termsText || 'No terms have been published yet.'}
            </div>
            <h2 className="text-h2 mt-6">Privacy Policy</h2>
            <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
              {terms.privacyText || 'No privacy policy has been published yet.'}
            </div>
            <div className="mt-6 flex justify-end">
              <Button size="sm" onClick={() => setShowTerms(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  children,
  className,
}: {
  label: React.ReactNode;
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className ? `space-y-1 ${className}` : 'space-y-1'}>
      <label className="text-sm font-medium" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}
